import { useGetLeadsSummary, type GetLeadsSummaryParams } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export type SummaryFilters = Omit<GetLeadsSummaryParams, "from" | "to">;

interface PerformanceSummaryProps {
  fromIso: string;
  toIso: string;
  fromLabel: string;
  toLabel: string;
  filters?: SummaryFilters;
  filtersActive?: boolean;
}

export function PerformanceSummary({
  fromIso,
  toIso,
  fromLabel,
  toLabel,
  filters,
  filtersActive,
}: PerformanceSummaryProps) {
  const { data: summary, isLoading } = useGetLeadsSummary({ from: fromIso, to: toIso, ...(filters ?? {}) });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Performance Summary
          {filtersActive && (
            <span className="rounded-full bg-primary/10 text-primary text-[10px] font-medium px-2 py-0.5 uppercase tracking-wide">
              Filtered
            </span>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {isLoading || !summary
            ? "Loading…"
            : `${summary.period.leadsReceived} new leads from ${fromLabel} to ${toLabel}${filtersActive ? " (matching active filters)" : ""}`}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading || !summary ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {summary.period.funnel.map((step) => (
              <div key={step.label} className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">{step.label}</div>
                <div className="text-2xl font-semibold">{step.count}</div>
                {step.conversionFromTotal != null && (
                  <div className="text-xs text-muted-foreground">{step.conversionFromTotal}% of total</div>
                )}
              </div>
            ))}
            <div className="rounded-md border p-3 border-destructive/30">
              <div className="text-xs text-muted-foreground">Closed Lost</div>
              <div className="text-2xl font-semibold">{summary.period.closedLost}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
